import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ImageSwiperRange } from '@/components/ImageSwiperRange/ImageSwiperRange';
import { amplitudeModule } from '@/modules/amplitude';
import { queryParamsModule } from '@/modules/queryParams';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { getMostPreferredCategory } from '@/pages/Onboarding/utils/getMostPreferredCategory';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataKeyType, LayoutDataType } from '../../types';

import './AiCharacteristic.scss';
import { getVariants } from './utils/getVariants';

export const OnboardingReplikaAiCharacteristic: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'AICharacteristic',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const categories = form.watch('categories');

  const priritizedCategories = getMostPreferredCategory(categories);

  const aiCharacteristic = form.watch('ai_characteristic');
  const aiCharacteristicData = getVariants(priritizedCategories.key);
  const aiCharacteristicIndexInForm = aiCharacteristicData.findIndex(
    (data) => data.label === aiCharacteristic,
  );
  const defaultAiCharacteristicIndex =
    aiCharacteristicIndexInForm === -1 ? 1 : aiCharacteristicIndexInForm;

  React.useEffect(() => {
    form.setValue(
      'ai_characteristic',
      aiCharacteristicData[defaultAiCharacteristicIndex].label,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRangeInput = (e: CustomEvent) => {
    const value = e.detail.value || (0 as number);
    const currentCharacteristic = aiCharacteristicData[value].label;

    form.setValue('ai_characteristic', currentCharacteristic);
  };

  const onRangeChange = (e: CustomEvent) => {
    const value = (e.detail.value as number) || 0;

    queryParamsModule.setParam<LayoutDataKeyType>(
      'ai_characteristic',
      aiCharacteristicData[value].label,
    );
  };

  const handleNextStep = (): void => {
    userService.createOrUpdate({
      onboarding_data: {
        ai_characteristic: aiCharacteristic,
      },
    });
    wizard.nextStep();
  };

  const handleBackStep = (): void => {
    wizard.goToStep('preferences');
  };

  return (
    <OnboardingPage className="onboarding-ai-characteristic">
      <OnboardingNavigation onBack={handleBackStep} />

      <IonContent className="content">
        <OnboardingTitle className="title">
          What kind of personality attracts you more?
        </OnboardingTitle>
        <ImageSwiperRange
          defaultIndex={defaultAiCharacteristicIndex}
          onRangeInput={onRangeInput}
          onRangeChange={onRangeChange}
          pictures={aiCharacteristicData}
        />
      </IonContent>

      <OnboardingFooter
        onContinue={handleNextStep}
        disabled={!aiCharacteristic}
      />
    </OnboardingPage>
  );
};
